import { graphql } from 'react-relay';
import React, { useContext, useEffect } from 'react';
import type { FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Link from '@atlaskit/link';
import { cssMap } from '@atlaskit/css';
import { Anchor } from '@atlaskit/primitives/compiled';

import { fg } from '@confluence/feature-gating';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { WIKI } from '@confluence/named-routes/entry-points/routes.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { WhiteboardsActiveBoardLimitModal } from '@confluence/change-edition/entry-points/WhiteboardsActiveBoardLimitModal.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import {
	WHITEBOARD_CREATION_PAYWALL_SOURCE_PREFIX,
	WHITEBOARD_PAYWALL_UPSELL_ACTION_SUBJECT_ID,
	WHITEBOARD_PAYWALL_INFO_ACTION_SUBJECT_ID,
} from '@confluence/whiteboard-utils/entry-points/whiteboardConstants.ts';
import { CONTENT_CREATION_PAYWALL_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceFailure } from '@confluence/experience-tracker/entry-points/component.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type { WhiteboardCreationLinkOptions } from '../../CreateContentRouteComponent/index.ts';
import { PaywallStatus } from '../../createContentInputTypes.ts';

import type { WhiteboardCreationPaywallDialogWhiteboardCreationPaywallContentToDisableQuery as WhiteboardCreationPaywallDialogWhiteboardCreationPaywallContentToDisableQuery$data } from './__types__/WhiteboardCreationPaywallDialogWhiteboardCreationPaywallContentToDisableQuery.ts';
const SCREEN_NAME = `${WHITEBOARD_CREATION_PAYWALL_SOURCE_PREFIX}upfrontDialog`;

const i18n = defineMessages({
	body: {
		id: 'create-blank-fabric-page.whiteboard-creation-paywall-dialog.body',
		defaultMessage:
			'You have a limit of three editable whiteboards. If you create a new whiteboard, an older whiteboard will become view-only.',
		description:
			'Text in the creation paywall modal describing the editable whiteboard limit and what will happen if you create a new whiteboard',
	},
	// For feature flag: FREE edition variant (three whiteboards)
	bodyFree: {
		id: 'create-blank-fabric-page.whiteboard-creation-paywall-dialog.body-free',
		defaultMessage:
			'You have a limit of three editable whiteboards. If you create a new whiteboard, an older whiteboard will become view-only.',
		description:
			'Text in the creation paywall modal for FREE edition describing the editable whiteboard limit',
	},
	// For feature flag: STANDARD edition variant (ten whiteboards)
	bodyStandard: {
		id: 'create-blank-fabric-page.whiteboard-creation-paywall-dialog.body-standard',
		defaultMessage:
			'You have a limit of ten editable whiteboards. If you create a new whiteboard, an older whiteboard will become view-only.',
		description:
			'Text in the creation paywall modal for STANDARD edition describing the editable whiteboard limit',
	},
	bodyUpsell: {
		id: 'create-blank-fabric-page.whiteboard-creation-paywall-dialog.bodyUpsell',
		defaultMessage: 'To create unlimited whiteboards, upgrade to Premium.',
		description:
			'Text in the creation paywall modal describing how to bypass the whiteboard creation limit',
	},
	bodyAdminStandard: {
		id: 'create-blank-fabric-page.whiteboard-creation-paywall-dialog.body-admin-standard',
		defaultMessage:
			'You can only edit ten whiteboards at a time. Until you upgrade, your oldest boards will become view-only as you create new ones.',
		description:
			'Text in the creation paywall modal for admins on STANDARD edition describing the editable whiteboard limit',
	},
	bodyEndUserStandard: {
		id: 'create-blank-fabric-page.whiteboard-creation-paywall-dialog.body-end-user-standard',
		defaultMessage:
			'You can only edit ten whiteboards at a time. Until you upgrade through your admin, your oldest boards will become view-only as you create new ones.',
		description:
			'Text in the creation paywall modal for end users on STANDARD edition describing the editable whiteboard limit',
	},
	bodyAdminFree: {
		id: 'create-blank-fabric-page.whiteboard-creation-paywall-dialog.body-admin-free',
		defaultMessage:
			'You can only edit three whiteboards at a time. Until you upgrade, your oldest boards will become view-only as you create new ones.',
		description:
			'Text in the creation paywall modal for admins on FREE edition describing the editable whiteboard limit',
	},
	bodyEndUserFree: {
		id: 'create-blank-fabric-page.whiteboard-creation-paywall-dialog.body-end-user-free',
		defaultMessage:
			'You can only edit three whiteboards at a time. Until you upgrade through your admin, your oldest boards will become view-only as you create new ones.',
		description:
			'Text in the creation paywall modal for end users on FREE edition describing the editable whiteboard limit',
	},
	createWhiteboard: {
		id: 'create-blank-fabric-page.whiteboard-creation-paywall-dialog.createWhiteboard',
		defaultMessage: 'Create whiteboard',
		description: 'Text for a link that will proceed to create a new whiteboard',
	},
	defaultTitle: {
		id: 'create-blank-fabric-page.whiteboard-creation-paywall-dialog.defaultWhiteboardName',
		defaultMessage: 'An older board will become view-only',
		description:
			'A modal dialog title explaining that an older whiteboard could become view-only based on actions taken within the modal',
	},
	learnMore: {
		id: 'create-blank-fabric-page.whiteboard-creation-paywall-dialog.learnMore',
		defaultMessage: 'Learn about usage',
		description: 'Text for a link that leads to documentation about whiteboard usage limits',
	},
	title: {
		id: 'create-blank-fabric-page.whiteboard-creation-paywall-dialog.title',
		defaultMessage: '{whiteboardName} will become view-only',
		description:
			'A modal dialog title explaining which whiteboard could become view-only based on actions taken within the modal',
	},
});

type DialogTitleProps = {
	href?: string | null;
	name?: string | null;
};

const whiteboardLinkStyles = cssMap({
	root: {
		textDecoration: 'none',

		'&:hover': {
			textDecoration: 'underline',
			cursor: 'pointer',
		},
	},
});

const DialogTitle = ({ href, name }: DialogTitleProps) => {
	const renderWhiteboardLink = (url: string, whiteboardName: string) => {
		if (fg('ten_active_boards_in_standard')) {
			return (
				<Anchor xcss={whiteboardLinkStyles.root} href={url}>
					{whiteboardName}
				</Anchor>
			);
		}

		return (
			<Link href={url} target="_blank">
				{whiteboardName}
			</Link>
		);
	};

	const getTitleText = () => {
		if (!name) {
			return <FormattedMessage {...i18n.defaultTitle} tagName="span" />;
		}

		const whiteboardName = href ? renderWhiteboardLink(`${WIKI.toUrl()}${href}`, name) : name;

		return <FormattedMessage {...i18n.title} values={{ whiteboardName }} />;
	};

	return <span data-testid="paywall-title">{getTitleText()}</span>;
};

export type WhiteboardCreationPaywallDialogProps = {
	onClose: () => void;
	onCloseComplete: () => void;
	buildWhiteboardCreationLink: (
		creationOverrides?: Partial<WhiteboardCreationLinkOptions>,
		sourceSuffix?: string,
	) => string;
};

export const WhiteboardCreationPaywallDialog: FC<WhiteboardCreationPaywallDialogProps> = ({
	onClose,
	onCloseComplete,
	buildWhiteboardCreationLink,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { push } = useRouteActions();
	const sessionData = useSessionData();
	const edition = sessionData?.edition;
	const { isSiteAdmin } = useContext(SPAViewContext);

	const { data, loading, error } =
		useQueryInterop<WhiteboardCreationPaywallDialogWhiteboardCreationPaywallContentToDisableQuery$data>(
			graphql`
				query WhiteboardCreationPaywallDialogWhiteboardCreationPaywallContentToDisableQuery {
					paywallContentToDisable(contentType: "whiteboard") {
						deactivationIdentifier
						link
						title
					}
				}
			`,
			{
				fetchPolicy: 'no-cache',
			},
		);
	const { link, title, deactivationIdentifier } = data?.paywallContentToDisable || {};

	const creationHref = buildWhiteboardCreationLink(
		{
			deactivationIdentifier,
			paywallStatus: PaywallStatus.ACTIVE,
		},
		'-creationPaywall',
	);

	useEffect(() => {
		// if the query has finished and there is no deactivationIdentifier, then we opened this modal due to caching issues and should proceed directly to creation
		if (!loading && !error && !deactivationIdentifier) {
			push(creationHref);
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'autoCreate',
					actionSubject: 'activeSlotAvailableAfterRenderingCreationPaywall',
					source: SCREEN_NAME,
				},
			}).fire();

			onClose();
			onCloseComplete();
		}
	}, [
		deactivationIdentifier,
		loading,
		error,
		creationHref,
		onClose,
		onCloseComplete,
		push,
		createAnalyticsEvent,
	]);

	const creationLink = {
		actionSubjectId: 'whiteboardPaywallCreateNewWhiteboardButton',
		messageDescriptor: i18n.createWhiteboard,
		href: creationHref,
		onClick: () => {
			onClose();
			onCloseComplete();
		},
		target: '_self',
	};

	const learnMoreLink = {
		actionSubjectId: WHITEBOARD_PAYWALL_INFO_ACTION_SUBJECT_ID,
		href: 'https://support.atlassian.com/confluence-cloud/docs/understand-confluence-whiteboards-plans-and-features/',
		messageDescriptor: i18n.learnMore,
	};

	let bodyMessage = i18n.body;

	if (fg('ten_active_boards_in_standard')) {
		if (edition === ConfluenceEdition.STANDARD) {
			bodyMessage = isSiteAdmin ? i18n.bodyAdminStandard : i18n.bodyEndUserStandard;
		} else {
			bodyMessage = isSiteAdmin ? i18n.bodyAdminFree : i18n.bodyEndUserFree;
		}
	}

	return (
		<WhiteboardsActiveBoardLimitModal
			learnMoreLink={learnMoreLink}
			onClose={onClose}
			onCloseComplete={onCloseComplete}
			secondaryLink={creationLink}
			source={SCREEN_NAME}
			title={<DialogTitle href={link} name={title} />}
			upgradeEditionActionSubjectId={WHITEBOARD_PAYWALL_UPSELL_ACTION_SUBJECT_ID}
		>
			<FormattedMessage {...bodyMessage} tagName="p" />
			{!fg('ten_active_boards_in_standard') && (
				<FormattedMessage {...i18n.bodyUpsell} tagName="p" />
			)}
			{error && (
				<ErrorDisplay error={error}>
					<ExperienceFailure name={CONTENT_CREATION_PAYWALL_EXPERIENCE} error={error} />
				</ErrorDisplay>
			)}
		</WhiteboardsActiveBoardLimitModal>
	);
};
